import React from 'react'
import { ArrowDown } from 'lucide-react'
import DashboardCard from '@/components/dashboard/DashboardCard'
import { scrollToSection } from '@/components/market/MarketHeader'
import { TONE_STYLE } from '@/lib/market'
import { cn } from '@/lib/utils'
import type { MarketTile } from '@/types/market'

/**
 * The five-second answer: five server tiles over the next `windowNights` nights. Each prints its
 * value and its evidence sentence as sent; `figure` exists for tests and is never rendered. Only a
 * non-neutral tone colours anything, and then only a hairline across the top of the cell.
 */
export default function SummaryStrip({ tiles, windowNights }: { tiles: MarketTile[]; windowNights: number }) {
  return (
    <DashboardCard title={`Next ${windowNights} nights`} padded={false}>
      <div className="grid grid-cols-2 lg:grid-cols-5">
        {tiles.map((tile, i) => {
          const last = i === tiles.length - 1
          const borders = cn(
            'border-border/40',
            i % 2 === 0 && !last && 'border-r',
            i >= 2 && 'border-t',
            'lg:border-t-0 lg:border-r-0',
            i > 0 && 'lg:border-l',
            last && tiles.length % 2 === 1 && 'col-span-2 lg:col-span-1'
          )
          const body = (
            <>
              {tile.tone !== 'neutral' && (
                <span
                  aria-hidden="true"
                  className={cn('absolute inset-x-0 top-0 h-0.5', TONE_STYLE[tile.tone].accent)}
                />
              )}
              <p className="min-h-[2.5em] text-[11px] font-semibold uppercase leading-[1.25] tracking-[0.1em] text-muted-foreground">
                {tile.label}
              </p>
              <p className="mt-1.5 flex items-center gap-1.5 text-xl font-bold tracking-tight text-foreground tabular-nums">
                {tile.value}
                {tile.key === 'market_moves' && (
                  <ArrowDown className="h-3.5 w-3.5 text-muted-foreground/60 transition-transform group-hover:translate-y-0.5" />
                )}
              </p>
              <p className="mt-1 text-[11px] leading-snug text-muted-foreground">{tile.detail}</p>
            </>
          )
          if (tile.key === 'market_moves') {
            return (
              <a
                key={tile.key}
                href="#market-moves"
                onClick={(e) => scrollToSection(e, 'market-moves')}
                className={cn(
                  'group relative block min-w-0 cursor-pointer px-5 py-4 transition-colors hover:bg-muted/30',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/40',
                  borders
                )}
              >
                {body}
              </a>
            )
          }
          return (
            <div key={tile.key} className={cn('relative min-w-0 px-5 py-4', borders)}>
              {body}
            </div>
          )
        })}
      </div>
    </DashboardCard>
  )
}
